import {
    FUNCTION_REQUIRED_INPUTS_MAP,
    FUNCTION_NODE_INPUT_LIMITS,
    FLOW_LINK_PLACEHOLDER_ICON,
} from '../constants/canvas';
import { getFunctionNodeEdgeSlotKeys } from '../constants/edgeInputSlots';
import { getSlotParamKey, isMultiImageFunctionKey } from './common';
import { getPreferredNodeImageUrl, getNodeImageSequenceLabel } from './execution';

export const mergeCreativeImgUploads = (manualUploads = [], linkedUploads = [], preferredOrder = []) => {
    const normalizedManualUploads = manualUploads.filter(Boolean);
    const normalizedLinkedUploads = linkedUploads
        .map((item) => item?.url || '')
        .filter(Boolean);
    const manualQueue = normalizedManualUploads.slice();
    const linkedQueue = normalizedLinkedUploads.slice();
    const mergedUploads = [];
    const consumeUrl = (queue, url) => {
        const index = queue.findIndex((item) => item === url);
        if (index < 0) return false;
        queue.splice(index, 1);
        mergedUploads.push(url);
        return true;
    };
    const consumeLinkedUrl = (edgeId) => {
        const linkedIndex = linkedUploads.findIndex((item) => String(item?.edgeId || '') === String(edgeId || ''));
        if (linkedIndex < 0) return false;
        const linkedUrl = linkedUploads[linkedIndex]?.url || '';
        const queueIndex = linkedQueue.findIndex((item) => item === linkedUrl);
        if (queueIndex < 0) return false;
        linkedQueue.splice(queueIndex, 1);
        mergedUploads.push(linkedUrl);
        return true;
    };

    (Array.isArray(preferredOrder) ? preferredOrder : []).filter(Boolean).forEach((orderItem) => {
        if (mergedUploads.length >= FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img) return;

        if (orderItem && typeof orderItem === 'object') {
            if (orderItem.type === 'linked' && consumeLinkedUrl(orderItem.edgeId)) return;
            if (orderItem.type === 'manual' && consumeUrl(manualQueue, orderItem.url)) return;
            if (consumeUrl(manualQueue, orderItem.url)) return;
            consumeUrl(linkedQueue, orderItem.url);
            return;
        }

        if (consumeUrl(manualQueue, orderItem)) return;
        consumeUrl(linkedQueue, orderItem);
    });

    manualQueue.concat(linkedQueue).forEach((url) => {
        if (mergedUploads.length < FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img) {
            mergedUploads.push(url);
        }
    });

    if (mergedUploads.length <= FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img) {
        return mergedUploads;
    }

    return [
        ...mergedUploads.slice(0, FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img - 1),
        mergedUploads[mergedUploads.length - 1],
    ];
};

export const extractCreativeImgManualUploads = (storedUploads = [], linkedUploads = []) => {
    const linkedUrlCountMap = linkedUploads.reduce((acc, item) => {
        const url = item?.url || '';
        if (!url) return acc;
        acc.set(url, (acc.get(url) || 0) + 1);
        return acc;
    }, new Map());

    return (Array.isArray(storedUploads) ? storedUploads : [])
        .filter(Boolean)
        .filter((url) => {
            const remainCount = linkedUrlCountMap.get(url) || 0;
            if (remainCount <= 0) return true;
            linkedUrlCountMap.set(url, remainCount - 1);
            return false;
        })
        .slice(0, FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img);
};

export const getPanelStateObject = (node) => (
    node?.data?.panelState && typeof node.data.panelState === 'object'
        ? node.data.panelState
        : {}
);

export const getCreativeImgOccupiedCount = (node, edges = []) => {
    if (!node || !isMultiImageFunctionKey(node?.data?.functionKey)) {
        return 0;
    }

    if (node?.data?.functionKey === 'templateReplace') {
        const panelState = getPanelStateObject(node);
        const manualUploads = Array.isArray(panelState.manualUploads)
            ? panelState.manualUploads
            : [];
        const linkedSlotSources = (
            panelState.linkedSlotSources
            && typeof panelState.linkedSlotSources === 'object'
            && !Array.isArray(panelState.linkedSlotSources)
        ) ? panelState.linkedSlotSources : {};
        const manualSourceCount = linkedSlotSources.source?.edgeId ? 0 : (manualUploads[0] ? 1 : 0);
        const manualReferenceCount = manualUploads.slice(1).filter(Boolean).length;
        const incomingEdgeCount = (Array.isArray(edges) ? edges : []).filter((edge) => edge.target === node.id).length;

        return manualSourceCount + manualReferenceCount + incomingEdgeCount;
    }

    const panelState = getPanelStateObject(node);
    const linkedUploads = Array.isArray(panelState.linkedUploads) ? panelState.linkedUploads : [];
    const storedUploads = Array.isArray(panelState.manualUploads)
        ? panelState.manualUploads
        : Array.isArray(panelState.uploads)
            ? panelState.uploads
            : Array.isArray(node?.data?.inputParams?.imageUrls)
                ? node.data.inputParams.imageUrls
                : [];
    const manualUploadCount = extractCreativeImgManualUploads(storedUploads, linkedUploads).length;
    const incomingEdgeCount = (Array.isArray(edges) ? edges : []).filter((edge) => edge.target === node.id).length;

    return manualUploadCount + incomingEdgeCount;
};

export const getLinkedSlotSources = (node) => {
    const linkedSlotSources = getPanelStateObject(node).linkedSlotSources;
    return (
        linkedSlotSources
        && typeof linkedSlotSources === 'object'
        && !Array.isArray(linkedSlotSources)
    )
        ? linkedSlotSources
        : {};
};

export const getSlotUploadValue = (node, slotKey) => {
    if (node?.data?.functionKey === 'templateReplace') {
        const panelState = getPanelStateObject(node);
        const linkedSlotSources = (
            panelState.linkedSlotSources
            && typeof panelState.linkedSlotSources === 'object'
            && !Array.isArray(panelState.linkedSlotSources)
        ) ? panelState.linkedSlotSources : {};
        if (linkedSlotSources[slotKey]?.url) {
            return linkedSlotSources[slotKey].url;
        }
        const manualUploads = Array.isArray(panelState.manualUploads)
            ? panelState.manualUploads
            : [];
        if (slotKey === 'source') {
            return manualUploads[0] || node?.data?.inputParams?.srcImage || '';
        }
        if (slotKey === 'reference') {
            return manualUploads[1] || node?.data?.inputParams?.refImage || '';
        }
    }

    const panelState = getPanelStateObject(node);
    const uploads = panelState.uploads && typeof panelState.uploads === 'object'
        ? panelState.uploads
        : {};
    const paramKey = getSlotParamKey(node?.data?.functionKey, slotKey);

    return uploads[slotKey] || (paramKey ? node?.data?.inputParams?.[paramKey] : '') || '';
};

export const getTemplateReplaceReferenceManualCount = (node) => {
    const panelState = getPanelStateObject(node);
    const manualUploads = Array.isArray(panelState.manualUploads)
        ? panelState.manualUploads
        : [];

    return manualUploads.slice(1).filter(Boolean).length;
};

export const getTemplateReplaceReferenceEdgeCount = (nodeId, edges = []) => (
    (Array.isArray(edges) ? edges : []).filter((edge) => (
        String(edge.target) === String(nodeId)
        && String(edge.data?.inputSlotKey || '') === 'reference'
    )).length
);

export const getNodeValidationError = (node) => {
    if (!node) return '';

    if (node.type === 'imageNode') {
        return node?.data?.src ? '' : `${node?.data?.label || '图片节点'}缺少图片`;
    }

    if (node.type !== 'functionNode') {
        return '';
    }

    const functionKey = node?.data?.functionKey || '';
    const nodeLabel = node?.data?.label || '功能节点';
    const inputParams = node?.data?.inputParams || {};

    if (functionKey === 'creativeTxt2Img') {
        return String(inputParams.prompt || '').trim() ? '' : `${nodeLabel}缺少提示词`;
    }

    if (functionKey === 'creativeImg2Img') {
        const hasPrompt = String(inputParams.prompt || '').trim().length > 0;
        const hasImageUrls = Array.isArray(inputParams.imageUrls) && inputParams.imageUrls.filter(Boolean).length > 0;
        return (hasPrompt || hasImageUrls) ? '' : `${nodeLabel}缺少提示词或参考图`;
    }

    if (functionKey === 'templateReplace') {
        const imageUrls = Array.isArray(inputParams.imageUrls)
            ? inputParams.imageUrls.filter(Boolean)
            : [];
        if (imageUrls.length === 0) {
            return `${nodeLabel}缺少模特图`;
        }
        if (imageUrls.length < 2) {
            return `${nodeLabel}缺少后面的图`;
        }
        return String(inputParams.prompt || '').trim() ? '' : `${nodeLabel}缺少提示词`;
    }

    if (functionKey === 'repose') {
        const sourceValue = getSlotUploadValue(node, 'source');
        const poseMode = inputParams.mode || node?.data?.panelState?.mode || 'ref';
        if (!sourceValue) {
            return `${nodeLabel}缺少源素材`;
        }
        if (poseMode === 'ref') {
            return getSlotUploadValue(node, 'reference') ? '' : `${nodeLabel}缺少参考图`;
        }
        return String(inputParams.prompt || '').trim() ? '' : `${nodeLabel}缺少提示词`;
    }

    if (functionKey === 'relight') {
        const sourceValue = getSlotUploadValue(node, 'source');
        const referenceValue = getSlotUploadValue(node, 'reference');
        const matchValue = getSlotUploadValue(node, 'match');
        const relightMode = inputParams.mode || node?.data?.panelState?.mode || 'single';
        if (!sourceValue) {
            return `${nodeLabel}缺少源素材`;
        }
        if (relightMode === 'suit') {
            if (!referenceValue && !matchValue) {
                return `${nodeLabel}缺少上装或下装`;
            }
        } else if (!referenceValue) {
            return relightMode === 'material' ? `${nodeLabel}缺少面料图` : `${nodeLabel}缺少参考图`;
        }
        if (relightMode === 'material' && !String(inputParams.targetArea || '').trim()) {
            return `${nodeLabel}缺少换色区域`;
        }
        return '';
    }

    if (functionKey === 'shoesRefiner') {
        if (!getSlotUploadValue(node, 'source')) {
            return `${nodeLabel}缺少源素材`;
        }
        if (!getSlotUploadValue(node, 'commodity')) {
            return `${nodeLabel}缺少商品图`;
        }
        return '';
    }

    const requiredInputs = FUNCTION_REQUIRED_INPUTS_MAP[functionKey] || [];
    const missingLabels = requiredInputs
        .filter((item) => !getSlotUploadValue(node, item.key))
        .map((item) => item.label);

    return missingLabels.length > 0
        ? `${nodeLabel}缺少${missingLabels.join('、')}`
        : '';
};

export const isSlotManuallyOccupied = (node, slotKey) => {
    if (node?.data?.functionKey === 'templateReplace') {
        const panelState = getPanelStateObject(node);
        const manualUploads = Array.isArray(panelState.manualUploads)
            ? panelState.manualUploads
            : [];
        if (slotKey === 'source') {
            return !!manualUploads[0];
        }
        if (slotKey === 'reference') {
            return getTemplateReplaceReferenceManualCount(node) >= (FUNCTION_NODE_INPUT_LIMITS.templateReplace - 1);
        }
    }

    const linkedState = getLinkedSlotSources(node)[slotKey];
    if (linkedState?.edgeId) return false;

    const uploadValue = getSlotUploadValue(node, slotKey);
    return !!uploadValue && uploadValue !== FLOW_LINK_PLACEHOLDER_ICON;
};

export const getAvailableEdgeSlotKeyForTarget = ({
    edges = [],
    targetNode,
    slotKeys = [],
    excludeEdgeId = null,
    preferredSlotKey = null,
}) => {
    const normalizedSlotKeys = slotKeys.filter(Boolean);
    if (normalizedSlotKeys.length === 0 || !targetNode) return null;

    if (targetNode?.data?.functionKey === 'templateReplace') {
        const filteredEdges = (Array.isArray(edges) ? edges : []).filter((edge) => edge.id !== excludeEdgeId);
        const sourceOccupied = filteredEdges.some((edge) => (
            String(edge.target) === String(targetNode.id)
            && String(edge.data?.inputSlotKey || '') === 'source'
        )) || isSlotManuallyOccupied(targetNode, 'source');
        const referenceOccupiedCount = getTemplateReplaceReferenceEdgeCount(targetNode.id, filteredEdges)
            + getTemplateReplaceReferenceManualCount(targetNode);

        if (!sourceOccupied && normalizedSlotKeys.includes('source')) {
            return 'source';
        }

        if (
            normalizedSlotKeys.includes('reference')
            && referenceOccupiedCount < (FUNCTION_NODE_INPUT_LIMITS.templateReplace - 1)
        ) {
            return 'reference';
        }

        return null;
    }

    const occupiedSlotKeys = new Set(
        edges
            .filter((edge) => edge.target === targetNode.id && edge.id !== excludeEdgeId)
            .map((edge) => edge.data?.inputSlotKey)
            .filter(Boolean),
    );

    normalizedSlotKeys.forEach((slotKey) => {
        if (isSlotManuallyOccupied(targetNode, slotKey)) {
            occupiedSlotKeys.add(slotKey);
        }
    });

    if (preferredSlotKey && normalizedSlotKeys.includes(preferredSlotKey) && !occupiedSlotKeys.has(preferredSlotKey)) {
        return preferredSlotKey;
    }

    return normalizedSlotKeys.find((slotKey) => !occupiedSlotKeys.has(slotKey)) || null;
};

// 同步连线目标节点的输入数据
export const syncConnectedTargetNodes = (nodes, edges, options = {}) => {
    const { sourceNodeId = null, targetNodeId = null } = options;
    const targetNodeIds = new Set(
        edges
            .filter((edge) => (
                (!sourceNodeId || edge.source === sourceNodeId)
                && (!targetNodeId || edge.target === targetNodeId)
            ))
            .map((edge) => edge.target),
    );
    if (targetNodeId) {
        targetNodeIds.add(targetNodeId);
    }

    if (targetNodeIds.size === 0) return nodes;

    const nodeMap = new Map(nodes.map((node) => [node.id, node]));

    return nodes.map((node) => {
        if (!targetNodeIds.has(node.id) || node.type !== 'functionNode') return node;

        const targetEdges = edges.filter((edge) => edge.target === node.id);
        if (isMultiImageFunctionKey(node.data?.functionKey)) {
            const previousPanelState = node.data?.panelState || {};
            if (node.data?.functionKey === 'templateReplace') {
                const previousManualUploads = Array.isArray(previousPanelState.manualUploads)
                    ? previousPanelState.manualUploads.slice(0, FUNCTION_NODE_INPUT_LIMITS.templateReplace)
                    : [
                        node.data?.inputParams?.srcImage || '',
                        node.data?.inputParams?.refImage || '',
                    ];
                const nextManualUploads = previousManualUploads.slice(0, FUNCTION_NODE_INPUT_LIMITS.templateReplace);
                const nextLinkedSlotSources = {};
                const getEdgeLinkedState = (edge) => {
                    const sourceNode = nodeMap.get(edge.source);
                    const actualUrl = getPreferredNodeImageUrl(sourceNode);
                    return {
                        edgeId: String(edge.id),
                        sourceNodeId: String(edge.source),
                        url: actualUrl || FLOW_LINK_PLACEHOLDER_ICON,
                        imageSequenceLabel: getNodeImageSequenceLabel(sourceNode),
                    };
                };
                const sourceEdge = targetEdges.find((edge) => String(edge.data?.inputSlotKey || '') === 'source');
                const referenceEdges = targetEdges.filter((edge) => String(edge.data?.inputSlotKey || '') === 'reference');
                const primaryReferenceEdge = referenceEdges[0];
                const extraReferenceEdges = referenceEdges.slice(1);
                const previousLinkedUploads = Array.isArray(previousPanelState.linkedUploads)
                    ? previousPanelState.linkedUploads
                    : [];
                const previousLinkedUploadMap = new Map(
                    previousLinkedUploads
                        .filter((item) => item?.edgeId)
                        .map((item) => [String(item.edgeId), item]),
                );
                const nextLinkedUploads = extraReferenceEdges.map((edge) => {
                    const previousLinkedUpload = previousLinkedUploadMap.get(String(edge.id));
                    const linkedState = getEdgeLinkedState(edge);

                    return {
                        ...linkedState,
                        url: linkedState.url || previousLinkedUpload?.url || FLOW_LINK_PLACEHOLDER_ICON,
                    };
                });

                if (sourceEdge) {
                    nextLinkedSlotSources.source = getEdgeLinkedState(sourceEdge);
                }
                if (primaryReferenceEdge) {
                    nextLinkedSlotSources.reference = getEdgeLinkedState(primaryReferenceEdge);
                }

                const sourceValue = nextLinkedSlotSources.source?.url || nextManualUploads[0] || '';
                const manualReferenceValues = nextManualUploads.slice(1).filter(Boolean);
                const linkedReferenceValues = [
                    nextLinkedSlotSources.reference?.url || '',
                    ...nextLinkedUploads.map((item) => item?.url || ''),
                ].filter(Boolean);
                const referenceHeadValue = linkedReferenceValues[0] || manualReferenceValues[0] || '';
                const extraReferenceValues = [
                    ...linkedReferenceValues.slice(1),
                    ...(linkedReferenceValues.length > 0 ? manualReferenceValues : manualReferenceValues.slice(1)),
                ].filter(Boolean);
                const normalizedManualUploads = [
                    nextLinkedSlotSources.source?.url ? '' : (nextManualUploads[0] || ''),
                    ...manualReferenceValues,
                ].slice(0, FUNCTION_NODE_INPUT_LIMITS.templateReplace);
                const mergedUploads = [
                    sourceValue ? { url: sourceValue, source: nextLinkedSlotSources.source ? 'line' : 'upload' } : null,
                    referenceHeadValue ? { url: referenceHeadValue, source: nextLinkedSlotSources.reference ? 'line' : 'upload' } : null,
                    ...extraReferenceValues.map((url, index) => ({ url, source: index < (linkedReferenceValues.length - 1) ? 'line' : 'upload' })),
                ].filter(Boolean);
                const mergedUploadItems = [
                    sourceValue
                        ? (nextLinkedSlotSources.source
                            ? { type: 'linked', edgeId: nextLinkedSlotSources.source.edgeId, url: sourceValue }
                            : { type: 'manual', url: sourceValue })
                        : null,
                    referenceHeadValue
                        ? (nextLinkedSlotSources.reference
                            ? { type: 'linked', edgeId: nextLinkedSlotSources.reference.edgeId, url: referenceHeadValue }
                            : { type: 'manual', url: referenceHeadValue })
                        : null,
                    ...nextLinkedUploads.map((item) => ({ type: 'linked', edgeId: item.edgeId, url: item.url })),
                    ...manualReferenceValues.map((url) => ({ type: 'manual', url })),
                ].filter(Boolean);
                const hasChanged = (
                    JSON.stringify(previousPanelState.linkedSlotSources || {}) !== JSON.stringify(nextLinkedSlotSources)
                    || JSON.stringify(previousPanelState.linkedUploads || []) !== JSON.stringify(nextLinkedUploads)
                    || JSON.stringify(previousPanelState.manualUploads || []) !== JSON.stringify(normalizedManualUploads)
                    || JSON.stringify(previousPanelState.uploads || []) !== JSON.stringify(mergedUploads)
                    || JSON.stringify(previousPanelState.uploadItems || []) !== JSON.stringify(mergedUploadItems)
                    || JSON.stringify(node.data?.inputParams?.imageUrls || []) !== JSON.stringify(mergedUploads)
                    || String(node.data?.inputParams?.srcImage || '') !== String(sourceValue || '')
                    || String(node.data?.inputParams?.refImage || '') !== String(referenceHeadValue || '')
                );

                if (!hasChanged) return node;

                return {
                    ...node,
                    data: {
                        ...node.data,
                        inputParams: {
                            ...(node.data?.inputParams || {}),
                            imageUrls: mergedUploads,
                            srcImage: sourceValue,
                            refImage: referenceHeadValue,
                        },
                        panelState: {
                            ...previousPanelState,
                            manualUploads: normalizedManualUploads,
                            linkedUploads: nextLinkedUploads,
                            linkedSlotSources: nextLinkedSlotSources,
                            uploadItems: mergedUploadItems,
                            uploads: mergedUploads,
                        },
                    },
                };
            }

            const previousLinkedUploads = Array.isArray(previousPanelState.linkedUploads)
                ? previousPanelState.linkedUploads
                : [];
            const previousLinkedUploadMap = new Map(
                previousLinkedUploads
                    .filter((item) => item?.edgeId)
                    .map((item) => [String(item.edgeId), item]),
            );
            const linkedUploads = targetEdges.map((edge) => {
                const sourceNode = nodeMap.get(edge.source);
                const previousLinkedUpload = previousLinkedUploadMap.get(String(edge.id));
                const actualUrl = getPreferredNodeImageUrl(sourceNode);
                return {
                    edgeId: String(edge.id),
                    sourceNodeId: edge.source,
                    url: actualUrl || previousLinkedUpload?.url || FLOW_LINK_PLACEHOLDER_ICON,
                    imageSequenceLabel: getNodeImageSequenceLabel(sourceNode),
                };
            });
            const manualUploads = Array.isArray(previousPanelState.manualUploads)
                ? previousPanelState.manualUploads.filter(Boolean)
                : extractCreativeImgManualUploads(
                    Array.isArray(previousPanelState.uploads)
                        ? previousPanelState.uploads
                        : (Array.isArray(node.data?.inputParams?.imageUrls)
                            ? node.data.inputParams.imageUrls
                            : []),
                    linkedUploads,
                );
            const previousOrder = Array.isArray(previousPanelState.uploadItems)
                ? previousPanelState.uploadItems
                : Array.isArray(previousPanelState.uploads)
                ? previousPanelState.uploads.filter(Boolean)
                : [];
            const nextLinkedUploadMap = new Map(
                linkedUploads
                    .filter((item) => item?.edgeId)
                    .map((item) => [String(item.edgeId), item]),
            );
            const preferredOrder = previousOrder.map((url) => {
                const matchedPreviousLinkedUpload = previousLinkedUploads.find((item) => item?.url === url);
                const nextLinkedUpload = matchedPreviousLinkedUpload?.edgeId
                    ? nextLinkedUploadMap.get(String(matchedPreviousLinkedUpload.edgeId))
                    : null;

                return nextLinkedUpload?.url || url;
            });
            linkedUploads.forEach((item) => {
                if (!item?.edgeId || previousLinkedUploadMap.has(String(item.edgeId))) return;
                preferredOrder.push({
                    type: 'linked',
                    edgeId: item.edgeId,
                    url: item.url,
                });
            });
            const mergedUploads = mergeCreativeImgUploads(manualUploads, linkedUploads, preferredOrder);
            const mergedUploadItems = preferredOrder
                .map((item) => {
                    if (item && typeof item === 'object') {
                        if (item.type === 'linked') {
                            const nextLinkedUpload = nextLinkedUploadMap.get(String(item.edgeId || ''));
                            return nextLinkedUpload
                                ? { type: 'linked', edgeId: nextLinkedUpload.edgeId, url: nextLinkedUpload.url }
                                : null;
                        }
                        if (item.type === 'manual' && item.url) {
                            return { type: 'manual', url: item.url };
                        }
                    }
                    return item ? { type: 'manual', url: item } : null;
                })
                .filter(Boolean);
            const hasChanged = (
                JSON.stringify(previousLinkedUploads) !== JSON.stringify(linkedUploads)
                || JSON.stringify(previousPanelState.uploads || []) !== JSON.stringify(mergedUploads)
                || JSON.stringify(previousPanelState.uploadItems || []) !== JSON.stringify(mergedUploadItems)
                || JSON.stringify(node.data?.inputParams?.imageUrls || []) !== JSON.stringify(mergedUploads)
            );

            if (!hasChanged) return node;

            return {
                ...node,
                data: {
                    ...node.data,
                    inputParams: {
                        ...(node.data?.inputParams || {}),
                        imageUrls: mergedUploads,
                        srcImage: mergedUploads[0] || '',
                        refImage: mergedUploads[1] || '',
                    },
                    panelState: {
                        ...previousPanelState,
                        manualUploads,
                        linkedUploads,
                        uploadItems: mergedUploadItems,
                        uploads: mergedUploads,
                    },
                },
            };
        }

        const previousPanelState = node.data?.panelState || {};
        const previousLinkedSlotSources = (
            previousPanelState.linkedSlotSources
            && typeof previousPanelState.linkedSlotSources === 'object'
            && !Array.isArray(previousPanelState.linkedSlotSources)
        )
            ? previousPanelState.linkedSlotSources
            : {};
        const managedSlotKeys = getFunctionNodeEdgeSlotKeys(node.data?.functionKey, node);
        const nextUploads = {
            ...(previousPanelState.uploads || {}),
        };
        const nextInputParams = {
            ...(node.data?.inputParams || {}),
        };
        const nextLinkedSlotSources = {
            ...previousLinkedSlotSources,
        };
        let changed = false;

        managedSlotKeys.forEach((slotKey) => {
            const paramKey = getSlotParamKey(node.data?.functionKey, slotKey);
            if (!paramKey) return;
            const targetEdge = targetEdges.find((edge) => edge.data?.inputSlotKey === slotKey);
            const currentValue = nextUploads[slotKey] || nextInputParams[paramKey] || '';
            const previousLinkedState = previousLinkedSlotSources[slotKey];
            const previousLinkedUrl = previousLinkedState?.url || '';
            const wasLinkedControlled = !!previousLinkedUrl && currentValue === previousLinkedUrl;

            if (!targetEdge) {
                if (wasLinkedControlled) {
                    if ((nextUploads[slotKey] || '') !== '') {
                        nextUploads[slotKey] = '';
                        changed = true;
                    }

                    if ((nextInputParams[paramKey] || '') !== '') {
                        nextInputParams[paramKey] = '';
                        changed = true;
                    }
                }

                if (slotKey in nextLinkedSlotSources) {
                    delete nextLinkedSlotSources[slotKey];
                    changed = true;
                }
                return;
            }

            const sourceNode = nodeMap.get(targetEdge.source);
            const actualUrl = getPreferredNodeImageUrl(sourceNode);
            const nextUrl = actualUrl || FLOW_LINK_PLACEHOLDER_ICON;
            const nextLinkedState = {
                edgeId: String(targetEdge.id),
                sourceNodeId: String(targetEdge.source),
                url: nextUrl,
                imageSequenceLabel: getNodeImageSequenceLabel(sourceNode),
            };

            if ((nextUploads[slotKey] || '') !== nextUrl) {
                nextUploads[slotKey] = nextUrl;
                changed = true;
            }

            if ((nextInputParams[paramKey] || '') !== nextUrl) {
                nextInputParams[paramKey] = nextUrl;
                changed = true;
            }

            if (JSON.stringify(previousLinkedState || null) !== JSON.stringify(nextLinkedState)) {
                nextLinkedSlotSources[slotKey] = nextLinkedState;
                changed = true;
            }
        });

        if (!changed) return node;

        return {
            ...node,
            data: {
                ...node.data,
                inputParams: nextInputParams,
                panelState: {
                    ...previousPanelState,
                    uploads: nextUploads,
                    linkedSlotSources: nextLinkedSlotSources,
                },
            },
        };
    });
};

// 功能节点的连线输入被替换时，反向同步对应的图片源节点
export const syncLinkedImageSourceNodes = (nodes, edges, targetNodeId) => {
    const targetNode = nodes.find((node) => String(node.id) === String(targetNodeId));
    if (!targetNode || targetNode.type !== 'functionNode') {
        return { nodes, sourceNodeIds: [] };
    }

    const panelState = targetNode.data?.panelState || {};
    const linkedUploads = Array.isArray(panelState.linkedUploads)
        ? panelState.linkedUploads
        : [];
    const sourceUrlMap = new Map();

    edges
        .filter((edge) => String(edge.target) === String(targetNodeId))
        .forEach((edge) => {
            const sourceNode = nodes.find((node) => String(node.id) === String(edge.source));
            if (!sourceNode || sourceNode.type !== 'imageNode') return;

            let nextUrl = '';
            if (isMultiImageFunctionKey(targetNode.data?.functionKey)) {
                const linkedUpload = linkedUploads.find((item) => (
                    String(item?.edgeId || '') === String(edge.id)
                ));
                nextUrl = linkedUpload?.url || '';
                if (!nextUrl) {
                    const slotKey = edge.data?.inputSlotKey || '';
                    nextUrl = slotKey ? getSlotUploadValue(targetNode, slotKey) : '';
                }
            } else {
                const slotKey = edge.data?.inputSlotKey || '';
                nextUrl = slotKey ? getSlotUploadValue(targetNode, slotKey) : '';
            }

            if (!nextUrl || nextUrl === FLOW_LINK_PLACEHOLDER_ICON) return;
            if (!sourceUrlMap.has(String(sourceNode.id))) {
                sourceUrlMap.set(String(sourceNode.id), nextUrl);
            }
        });

    if (sourceUrlMap.size === 0) {
        return { nodes, sourceNodeIds: [] };
    }

    const changedSourceNodeIds = [];
    const nextNodes = nodes.map((node) => {
        const nextUrl = sourceUrlMap.get(String(node.id));
        if (!nextUrl || node.type !== 'imageNode' || node.data?.src === nextUrl) return node;

        changedSourceNodeIds.push(node.id);
        return {
            ...node,
            data: {
                ...node.data,
                src: nextUrl,
                imageAspectRatio: null,
                inputParams: {
                    ...(node.data?.inputParams || {}),
                    output: nextUrl,
                },
            },
        };
    });

    return {
        nodes: nextNodes,
        sourceNodeIds: changedSourceNodeIds,
    };
};